import React, { useEffect, useRef, useState } from 'react';
import { X, Volume2, VolumeX } from 'lucide-react';
import { IMAGES } from '../data/mock';

/*
  Cinematic teaser — a stills-driven “video” built from the
  production photography. Ken Burns motion, timed text
  reveals, dark cuts. Runs ~34 seconds then reveals a
  BOOK YOUR TRIAL call to action. When a real trailer
  file is ready, replace this component with a <video> tag
  pointing at the mp4.
*/

const SHOTS = [
  {
    id: 'title',
    kind: 'title',
    duration: 2600,
    line1: '1627.',
    line2: 'Bamberg.'
  },
  {
    id: 'cottage',
    kind: 'image',
    img: IMAGES.doorway,
    focus: '50% 50%',
    start: { scale: 1.05, x: -1, y: -1 },
    end: { scale: 1.18, x: 2, y: 1 },
    duration: 4200,
    caption: 'A tudor house.'
  },
  {
    id: 'hero',
    kind: 'image',
    img: IMAGES.hero,
    focus: '55% 55%',
    start: { scale: 1.02, x: 0, y: 0 },
    end: { scale: 1.15, x: -2, y: -1 },
    duration: 4200,
    caption: 'One table.'
  },
  {
    id: 'inquisitor',
    kind: 'image',
    img: IMAGES.inquisitor,
    focus: '50% 28%',
    start: { scale: 1.05, x: 0, y: 0 },
    end: { scale: 1.18, x: -1, y: 1 },
    duration: 4400,
    caption: 'The Inquisitor.'
  },
  {
    id: 'accused',
    kind: 'image',
    img: IMAGES.accused,
    focus: '58% 30%',
    start: { scale: 1.02, x: 0, y: 0 },
    end: { scale: 1.16, x: 1, y: -1 },
    duration: 4400,
    caption: 'Your hand on the table.'
  },
  {
    id: 'village',
    kind: 'image',
    img: IMAGES.village,
    focus: '50% 55%',
    start: { scale: 1.02, x: 0, y: 0 },
    end: { scale: 1.14, x: 0, y: 1 },
    duration: 3800,
    caption: 'A village. Ash. A key.'
  },
  {
    id: 'quote',
    kind: 'title',
    duration: 3400,
    line1: '“Confess,',
    line2: 'and you can die clean.”'
  },
  {
    id: 'cta',
    kind: 'cta',
    duration: 6000,
    line1: 'ASH',
    line2: 'The Witch Trials of Drudenhaus',
    question: "Can you prove you're not a witch?"
  }
];

const TrailerModal = ({ open, onClose, onBookClick }) => {
  const [index, setIndex] = useState(0);
  const [audioOn, setAudioOn] = useState(false);
  const audioRef = useRef(null);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  // Start playback when opened
  useEffect(() => {
    if (!open) return;
    setIndex(0);

    // Chain scheduled shot changes
    let elapsed = 0;
    SHOTS.forEach((shot, i) => {
      elapsed += shot.duration;
      const t = setTimeout(() => {
        setIndex(Math.min(i + 1, SHOTS.length - 1));
      }, elapsed);
      timers.current.push(t);
    });

    // Escape closes
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Ambient candle-hum tone via WebAudio (subtle, opt-in)
  useEffect(() => {
    if (!open || !audioOn) return;
    let ctx;
    let stopped = false;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      ctx = new AudioCtx();

      // Low drone
      const drone = ctx.createOscillator();
      drone.type = 'sine';
      drone.frequency.value = 55;
      const droneGain = ctx.createGain();
      droneGain.gain.value = 0.0;
      droneGain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 1.2);
      drone.connect(droneGain).connect(ctx.destination);
      drone.start();

      // Higher shimmer
      const shimmer = ctx.createOscillator();
      shimmer.type = 'triangle';
      shimmer.frequency.value = 165;
      const shimmerGain = ctx.createGain();
      shimmerGain.gain.value = 0.0;
      shimmerGain.gain.linearRampToValueAtTime(0.03, ctx.currentTime + 2);
      shimmer.connect(shimmerGain).connect(ctx.destination);
      shimmer.start();

      audioRef.current = { ctx, drone, shimmer, droneGain, shimmerGain };
    } catch (e) {
      /* silent */
    }

    return () => {
      if (stopped) return;
      stopped = true;
      const a = audioRef.current;
      if (a) {
        try {
          a.droneGain.gain.linearRampToValueAtTime(0, a.ctx.currentTime + 0.4);
          a.shimmerGain.gain.linearRampToValueAtTime(0, a.ctx.currentTime + 0.4);
          setTimeout(() => a.ctx.close(), 600);
        } catch (e) {}
      }
      audioRef.current = null;
    };
  }, [open, audioOn]);

  const handleClose = () => {
    clearTimers();
    setIndex(0);
    setAudioOn(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="ASH — teaser"
      className="fixed inset-0 z-[100] bg-[#050301]"
    >
      {/* Shots */}
      <div className="absolute inset-0 overflow-hidden">
        {SHOTS.map((shot, i) => (
          <Shot
            key={shot.id}
            active={i === index}
            shot={shot}
            onCtaBook={() => {
              handleClose();
              onBookClick && onBookClick();
            }}
          />
        ))}
      </div>

      {/* Ash particles */}
      <AshParticles />

      {/* Controls */}
      <button
        onClick={handleClose}
        aria-label="Close teaser"
        className="absolute top-5 right-5 z-20 w-10 h-10 flex items-center justify-center border border-[#c8935a]/40 text-[#c8935a] hover:bg-[#c8935a]/10 hover:text-[#f4e8ce] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <button
        onClick={() => setAudioOn((v) => !v)}
        aria-label={audioOn ? 'Mute ambient sound' : 'Play ambient sound'}
        title={audioOn ? 'Mute' : 'Play ambient sound'}
        className="absolute top-5 right-20 z-20 w-10 h-10 flex items-center justify-center border border-[#c8935a]/40 text-[#c8935a] hover:bg-[#c8935a]/10 hover:text-[#f4e8ce] transition-colors"
      >
        {audioOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>

      <button
        onClick={handleClose}
        className="absolute bottom-5 right-5 z-20 text-[10px] tracking-[0.4em] font-fell-sc uppercase text-[#8b7355] hover:text-[#f4e8ce] transition-colors"
      >
        Skip ›
      </button>

      {/* Progress indicator */}
      <div className="absolute bottom-5 left-5 right-24 z-20 flex gap-1.5">
        {SHOTS.map((_, i) => (
          <span
            key={i}
            className={`h-[2px] flex-1 transition-colors duration-500 ${
              i <= index ? 'bg-[#c8935a]' : 'bg-[#3a2818]'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Shot = ({ active, shot, onCtaBook }) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (active) {
      const t = setTimeout(() => setEntered(true), 40);
      return () => clearTimeout(t);
    }
    setEntered(false);
  }, [active]);

  if (shot.kind === 'title') {
    return (
      <div
        aria-hidden={!active}
        className={`absolute inset-0 flex flex-col items-center justify-center px-6 text-center transition-opacity duration-[1200ms] ${
          active ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <p
          className={`font-display text-[#f4e8ce] leading-tight transition-all duration-[1200ms] ${
            entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ fontSize: 'clamp(38px, 6vw, 92px)', letterSpacing: '0.06em' }}
        >
          {shot.line1}
        </p>
        <p
          className={`mt-4 font-fell italic text-[#d9c8a3] transition-all duration-[1400ms] delay-200 ${
            entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ fontSize: 'clamp(28px, 5vw, 68px)' }}
        >
          {shot.line2}
        </p>
      </div>
    );
  }

  if (shot.kind === 'cta') {
    return (
      <div
        aria-hidden={!active}
        className={`absolute inset-0 flex flex-col items-center justify-center px-6 text-center transition-opacity duration-[1200ms] ${
          active ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <p className="text-[10px] tracking-[0.5em] font-fell-sc uppercase text-[#c8935a]">
          Halloween 2026
        </p>
        <h2
          className="mt-6 font-display font-bold text-[#f4e8ce] ember-glow leading-[0.95]"
          style={{ fontSize: 'clamp(80px, 15vw, 220px)', letterSpacing: '0.1em' }}
        >
          {shot.line1}
        </h2>
        <p className="mt-3 font-fell italic text-[#d9c8a3] text-lg md:text-2xl">
          {shot.line2}
        </p>
        <p className="mt-8 font-fell italic text-[#e8dcc0] text-lg md:text-xl">
          {shot.question}
        </p>
        {active && (
          <button
            onClick={onCtaBook}
            className="mt-10 inline-flex items-center gap-3 px-10 py-4 border border-[#c8935a]/70 bg-black/30 hover:bg-[#c8935a]/10 text-[#e8dcc0] hover:text-[#f4e8ce] transition-colors font-fell-sc text-[11px] tracking-[0.4em] uppercase"
          >
            Book your trial
          </button>
        )}
      </div>
    );
  }

  // Image shot with Ken Burns
  const { start, end, duration } = shot;
  return (
    <div
      aria-hidden={!active}
      className={`absolute inset-0 transition-opacity ${
        active ? 'opacity-100 duration-[1400ms]' : 'opacity-0 duration-[900ms] pointer-events-none'
      }`}
    >
      <div
        role="img"
        aria-label={shot.caption}
        className="absolute inset-0 bg-cover"
        style={{
          backgroundImage: `url(${shot.img})`,
          backgroundPosition: shot.focus || '50% 50%',
          transform: entered
            ? `scale(${end.scale}) translate3d(${end.x}%, ${end.y}%, 0)`
            : `scale(${start.scale}) translate3d(${start.x}%, ${start.y}%, 0)`,
          transition: `transform ${duration}ms linear`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
      <div className="absolute inset-0 bg-black/10" />
      {shot.caption && (
        <div className="absolute bottom-16 left-0 right-0 flex justify-center px-6">
          <p
            className={`font-fell italic text-[#f4e8ce] text-center transition-all duration-[1200ms] delay-300 ${
              entered && active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
            style={{ fontSize: 'clamp(22px, 3.4vw, 42px)' }}
          >
            {shot.caption}
          </p>
        </div>
      )}
    </div>
  );
};

const AshParticles = () => {
  const particles = Array.from({ length: 30 });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
      {particles.map((_, i) => {
        const left = Math.random() * 100;
        const duration = 12 + Math.random() * 18;
        const delay = Math.random() * 20;
        const size = 1 + Math.random() * 2.5;
        const opacity = 0.15 + Math.random() * 0.45;
        return (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${left}%`,
              top: '-5%',
              width: size,
              height: size,
              background: '#d9a55e',
              opacity,
              filter: 'blur(0.5px)',
              animation: `ashDrift ${duration}s linear ${delay}s infinite`
            }}
          />
        );
      })}
    </div>
  );
};

export default TrailerModal;
